import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { Pencil, Settings, Users, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { useCareTeamChannel, useInbox } from '../../hooks/chat/useChatQueries';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { ConnectionStrip } from './ConnectionStrip';
import { InboxRow } from './InboxRow';
export function ChatInbox() {
  const location = useLocation();
  const currentPatientId =
    location.pathname.match(/^\/patients\/([^/]+)/)?.[1] ?? null;
  const navigate = useNavigate();
  const canManageGroups = useStore(state =>
    state.hasPermission(PermissionSlug.CHAT_EDIT_GROUPS)
  );
  const { data, isLoading, error } = useInbox();
  const openChatChannel = useStore(state => state.openChatChannel);
  const setChatView = useStore(state => state.setChatView);
  const closeChat = useStore(state => state.closeChat);
  const chatContextEpisodeId = useStore(state => state.chatContextEpisodeId);
  // `useCareTeamChannel` is gated on the episode id and on the chat flag, and
  // answers 404/403 as `null`, so no request fires without a chart on screen
  // and a non-member pins nothing.
  const { data: contextChannel } = useCareTeamChannel(
    chatContextEpisodeId ?? undefined
  );
  const rows = data ?? [];
  // Keep a retained episode from a chart remount from pinning a row for a
  // different patient, or a page that is not a patient chart.
  const isCurrentPatientChannel =
    currentPatientId !== null &&
    contextChannel?.patient_id === currentPatientId;
  const pinnedRow =
    isCurrentPatientChannel && contextChannel
      ? rows.find(r => r.channel_id === contextChannel.id)
      : undefined;
  const pinnedId = pinnedRow?.channel_id ?? null;
  const orderedRows = pinnedRow
    ? [pinnedRow, ...rows.filter(r => r.channel_id !== pinnedRow.channel_id)]
    : rows;

  return (
    <>
      <div className='flex items-center justify-between border-b px-3 py-2'>
        <h2 className='text-sm font-semibold'>Messages</h2>
        <div className='flex items-center gap-1'>
          <Button
            size='icon'
            variant='ghost'
            aria-label='New message'
            onClick={() => setChatView('new')}
          >
            <Pencil className='h-4 w-4' />
          </Button>
          {canManageGroups && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size='icon' variant='ghost' aria-label='Chat settings'>
                  <Settings className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onSelect={() => {
                    navigate('/group-manager', {
                      state: { from: location.pathname + location.search },
                    });
                    closeChat();
                  }}
                >
                  <Users className='mr-2 h-4 w-4' />
                  Manage Groups
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            size='icon'
            variant='ghost'
            aria-label='Close'
            onClick={closeChat}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>
      <ConnectionStrip />

      <div className='flex-1 overflow-y-auto'>
        {/* The flag went away after load; the launcher unmounts on the next
            /auth/me refresh, and until then the panel says so rather than
            claiming there are no conversations. Any other failure with no
            rows to show gets the same copy, not the empty state. */}
        {getHttpStatus(error) === 403 || (error && !data) ? (
          <p className='px-6 py-8 text-center text-sm text-muted-foreground'>
            Messages are not available.
          </p>
        ) : !isLoading && rows.length === 0 ? (
          <div className='flex h-full flex-col items-center justify-center gap-3 px-6 text-center'>
            <p className='text-sm text-muted-foreground'>
              No conversations yet
            </p>
            <Button size='sm' onClick={() => setChatView('new')}>
              New message
            </Button>
          </div>
        ) : (
          orderedRows.map(row => (
            <InboxRow
              key={row.channel_id}
              row={row}
              onOpen={openChatChannel}
              highlighted={row.channel_id === pinnedId}
            />
          ))
        )}
      </div>
    </>
  );
}
